'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph, ErrorGlyph, RetryGlyph, SuccessGlyph } from '../../../internal/icons.js';
import { Button, IconButton } from '../../actions/button/index.js';
import { Progress } from '../../data-display/progress-bar/index.js';
import { formatBytes } from '../file-upload/index.js';
const DEFAULT_MESSAGES = {
    title: 'Uploads',
    queued: 'Waiting',
    uploading: (p) => (p === undefined ? 'Uploading…' : `Uploading… ${p}%`),
    done: 'Uploaded',
    failed: 'Upload failed',
    cancelled: 'Cancelled',
    cancel: (name) => `Cancel upload of ${name}`,
    retry: 'Retry',
    retryLabel: (name) => `Retry upload of ${name}`,
    remove: (name) => `Remove ${name} from the list`,
    clearFinished: 'Clear finished',
    progressLabel: (name) => `Upload progress of ${name}`,
    totalLabel: 'Total upload progress',
    summary: (c) => [
        `${c.done} of ${c.total - c.cancelled} uploaded`,
        c.failed ? `${c.failed} failed` : '',
        c.cancelled ? `${c.cancelled} cancelled` : '',
    ]
        .filter(Boolean)
        .join(' · '),
    announceDone: (name) => `${name} uploaded.`,
    announceFailed: (name, error) => `${name} failed${error ? `: ${error}` : ''}.`,
    announceFinished: (c) => c.failed
        ? `Uploads finished: ${c.done} uploaded, ${c.failed} failed.`
        : `All uploads finished: ${c.done} uploaded.`,
    formatBytes,
};
const ACTIVE = new Set(['queued', 'uploading']);
/** Counts uploads by status. */
export function countUploads(items) {
    const c = {
        total: items.length,
        queued: 0,
        uploading: 0,
        done: 0,
        failed: 0,
        cancelled: 0,
    };
    for (const i of items)
        c[i.status]++;
    return c;
}
/**
 * Overall progress, 0–100, of the uploads that can still finish (cancelled
 * and failed ones are left out). Weighted by size when every upload has one.
 */
export function totalUploadProgress(items) {
    const live = items.filter((i) => i.status !== 'cancelled' && i.status !== 'failed');
    if (!live.length)
        return 0;
    const bySize = live.every((i) => (i.size ?? 0) > 0);
    let sum = 0;
    let weight = 0;
    for (const i of live) {
        const w = bySize ? i.size : 1;
        const p = i.status === 'done' ? 100 : i.status === 'uploading' ? (i.progress ?? 0) : 0;
        sum += w * Math.max(0, Math.min(100, p));
        weight += w;
    }
    return Math.round(sum / weight);
}
/**
 * The list of files being uploaded: per-file progress and status, cancel,
 * retry and remove, and a total summary. Completion and failures are
 * announced. The product does the transfer and reports progress back.
 */
export const UploadQueue = forwardRef(function UploadQueue({ items, onCancel, onRetry, onRemove, onClearFinished, showSummary = true, headingLevel = 3, messages: messagesProp, className, ...rest }, ref) {
    const m = { ...DEFAULT_MESSAGES, ...messagesProp };
    const Heading = `h${headingLevel}`;
    const id = useId();
    const titleId = `${id}-title`;
    const counts = countUploads(items);
    const active = counts.queued + counts.uploading;
    const finished = counts.done + counts.failed + counts.cancelled;
    const total = totalUploadProgress(items);
    // Announce transitions to done and failed, and the end of the batch.
    const [announcement, setAnnouncement] = useState('');
    const previous = useRef(null);
    useEffect(() => {
        const before = previous.current;
        previous.current = new Map(items.map((i) => [i.id, i.status]));
        if (!before)
            return;
        const parts = [];
        let changed = false;
        for (const i of items) {
            const was = before.get(i.id);
            if (was === i.status)
                continue;
            if (i.status === 'done')
                parts.push(m.announceDone(i.name));
            if (i.status === 'failed')
                parts.push(m.announceFailed(i.name, i.error));
            if (was !== undefined && ACTIVE.has(was) && !ACTIVE.has(i.status))
                changed = true;
        }
        if (changed && active === 0 && items.length > 1) {
            parts.push(m.announceFinished(counts));
        }
        if (parts.length)
            setAnnouncement(parts.join(' '));
        // Messages are read fresh each time; items drive the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);
    // When a row is removed with focus inside it, focus moves to the next row.
    const listRef = useRef(null);
    const headingRef = useRef(null);
    const removedAt = useRef(null);
    useLayoutEffect(() => {
        const at = removedAt.current;
        if (at === null)
            return;
        removedAt.current = null;
        const rows = listRef.current?.querySelectorAll(':scope > li') ?? [];
        const row = rows[Math.min(at, rows.length - 1)];
        const target = row?.querySelector('button:not(:disabled)');
        if (target)
            target.focus();
        else
            headingRef.current?.focus();
    }, [items]);
    const remove = (itemId, index) => {
        removedAt.current = index;
        onRemove?.(itemId);
    };
    return (_jsxs("section", { ref: ref, "aria-labelledby": showSummary ? titleId : undefined, "aria-label": showSummary ? undefined : m.title, ...rest, className: cx('mn-upload-queue', className), "data-active": active > 0 || undefined, children: [showSummary ? (_jsxs("header", { className: "mn-upload-queue__header", children: [_jsxs("div", { className: "mn-upload-queue__heading", children: [_jsx(Heading, { ref: headingRef, id: titleId, tabIndex: -1, className: "mn-upload-queue__title", children: m.title }), items.length ? _jsx("p", { className: "mn-upload-queue__summary", children: m.summary(counts) }) : null] }), onClearFinished && finished > 0 ? (_jsx(Button, { size: "xs", variant: "ghost", onClick: onClearFinished, children: m.clearFinished })) : null, active > 0 ? (_jsx(Progress, { className: "mn-upload-queue__total", size: "sm", value: total, label: m.totalLabel, valueText: `${total}%` })) : null] })) : null, _jsx("ul", { ref: listRef, className: "mn-upload-queue__list", children: items.map((item, index) => {
                    const isActive = ACTIVE.has(item.status);
                    const canRetry = (item.status === 'failed' || item.status === 'cancelled') && item.retryable !== false;
                    const percent = item.progress === undefined
                        ? undefined
                        : Math.round(Math.max(0, Math.min(100, item.progress)));
                    const statusText = item.status === 'queued'
                        ? m.queued
                        : item.status === 'uploading'
                            ? m.uploading(percent)
                            : item.status === 'done'
                                ? m.done
                                : item.status === 'failed'
                                    ? (item.error ?? m.failed)
                                    : m.cancelled;
                    return (_jsxs("li", { className: "mn-upload-queue__item", "data-status": item.status, children: [_jsx("span", { className: "mn-upload-queue__media", "aria-hidden": true, children: item.thumbnail ? (_jsx("img", { className: "mn-upload-queue__thumb", src: item.thumbnail, alt: "" })) : ((item.icon ?? _jsx(FileGlyph, {}))) }), _jsxs("div", { className: "mn-upload-queue__body", children: [_jsxs("p", { className: "mn-upload-queue__name", children: [_jsx("span", { className: "mn-upload-queue__filename", children: item.name }), item.size !== undefined ? (_jsx("span", { className: "mn-upload-queue__size", children: m.formatBytes(item.size) })) : null] }), _jsxs("p", { className: "mn-upload-queue__status", "data-status": item.status, children: [item.status === 'done' ? _jsx(SuccessGlyph, {}) : null, item.status === 'failed' ? _jsx(ErrorGlyph, {}) : null, _jsx("span", { children: item.detail ?? statusText })] }), item.status === 'uploading' ? (_jsx(Progress, { className: "mn-upload-queue__progress", size: "sm", value: percent ?? null, label: m.progressLabel(item.name) })) : null] }), _jsxs("div", { className: "mn-upload-queue__actions", children: [canRetry && onRetry ? (_jsx(Button, { size: "xs", variant: "ghost", leadingIcon: _jsx(RetryGlyph, {}), "aria-label": m.retryLabel(item.name), onClick: () => onRetry(item.id), children: m.retry })) : null, isActive && onCancel ? (_jsx(IconButton, { size: "xs", icon: _jsx(CloseGlyph, {}), label: m.cancel(item.name), onClick: () => onCancel(item.id) })) : null, !isActive && onRemove ? (_jsx(IconButton, { size: "xs", icon: _jsx(CloseGlyph, {}), label: m.remove(item.name), onClick: () => remove(item.id, index) })) : null] })] }, item.id));
                }) }), _jsx("span", { className: "mn-visually-hidden", "aria-live": "polite", "aria-atomic": "true", children: announcement })] }));
});
function FileGlyph() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinejoin: "round", "aria-hidden": true, focusable: false, children: [_jsx("path", { d: "M9 1.75H4.5a1.25 1.25 0 0 0-1.25 1.25v10a1.25 1.25 0 0 0 1.25 1.25h7a1.25 1.25 0 0 0 1.25-1.25V5.5L9 1.75Z" }), _jsx("path", { d: "M9 1.75V5.5h3.75" })] }));
}
//# sourceMappingURL=upload-queue.js.map